import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Restaurant, OpeningHour } from "@/lib/types";
import { apiRequest, safeParseResponse } from "@/lib/queryClient";
import { queryKeys, invalidateRestaurantDetail, invalidateAllRestaurantLists } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Copy, Check, Plus, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RestaurantFormOpeningHoursProps {
  restaurant: Restaurant;
  onDirtyChange: (isDirty: boolean) => void;
  onSavingChange: (isSaving: boolean) => void;
}

// Wochentage für die Anzeige
const WEEKDAYS = [
  "Montag", "Dienstag", "Mittwoch", "Donnerstag", 
  "Freitag", "Samstag", "Sonntag"
];

// Typ für verbesserte Öffnungszeiten mit Mittagspause
interface EnhancedOpeningHour {
  day: number;
  isOpen: boolean;
  start: string;
  end: string;
  hasPause: boolean;
  pauseStart: string;
  pauseEnd: string;
}

// Rückgabetyp der API für bessere Typsicherheit
interface OpeningHourApiResponse {
  openingHours?: OpeningHour[];
  data?: OpeningHour[];
  message?: string;
  error?: string;
}

// Erstelle Standardzeiten (auskommentiert, da nicht verwendet)
// const createDefaultHours = (): EnhancedOpeningHour[] => {
//   return WEEKDAYS.map((_, index) => ({
//     day: index + 1,
//     isOpen: false,
//     start: "10:00",
//     end: "22:00",
//     hasPause: false,
//     pauseStart: "15:00",
//     pauseEnd: "17:00"
//   }));
// };

// Konvertiere vorhandene Öffnungszeiten in das erweiterte Format
const convertOpeningHours = (hours: OpeningHour[]): EnhancedOpeningHour[] => {
  // Erstelle Map für einfacheren Zugriff nach Wochentag
  const hoursByDay = new Map<number, OpeningHour>();
  hours.forEach(hour => {
    const day = hour.weekday || hour.day || 0;
    if (day > 0) {
      hoursByDay.set(day, hour);
    }
  });

  // Erstelle erweiterte Öffnungszeiten für jeden Tag
  return WEEKDAYS.map((_, index) => {
    const day = index + 1;
    const existingHour = hoursByDay.get(day);
    
    if (existingHour) {
      return {
        day,
        isOpen: true,
        start: existingHour.openStart || existingHour.opening || "10:00",
        end: existingHour.openEnd || existingHour.closing || "22:00",
        hasPause: !!existingHour.pauseStart && !!existingHour.pauseEnd,
        pauseStart: existingHour.pauseStart || "15:00",
        pauseEnd: existingHour.pauseEnd || "17:00"
      };
    } else {
      return {
        day,
        isOpen: false,
        start: "10:00",
        end: "22:00",
        hasPause: false,
        pauseStart: "15:00",
        pauseEnd: "17:00"
      };
    }
  });
};

const RestaurantFormOpeningHours = ({ restaurant, onDirtyChange, onSavingChange }: RestaurantFormOpeningHoursProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [activeDay, setActiveDay] = useState<string>("uebersicht");
  
  // Direkter Zugriff auf aktuelle Öffnungszeiten vom Server
  const { data: currentHours = [], refetch: refetchHours } = useQuery<OpeningHour[]>({
    queryKey: queryKeys.restaurants.openingHours(restaurant.id),
    initialData: restaurant.openingHours || [],
    enabled: !!restaurant?.id
  });
  
  // Verwende lokalen Status für die Bearbeitung
  const [openingHours, setOpeningHours] = useState<EnhancedOpeningHour[]>(
    convertOpeningHours(currentHours)
  );
  
  // Aktualisiere lokalen Zustand, wenn sich die Daten vom Server ändern
  useEffect(() => {
    setOpeningHours(convertOpeningHours(currentHours));
  }, [currentHours]);
  
  // Aktualisiere isDirty-Status
  const checkForChanges = (): boolean => {
    const currentEnhanced = convertOpeningHours(currentHours);
    
    // Vergleiche jede Eigenschaft jedes Tages
    for (let i = 0; i < 7; i++) {
      const current = currentEnhanced[i];
      const edited = openingHours[i];
      
      if (current.isOpen !== edited.isOpen) return true;
      if (current.isOpen && edited.isOpen) {
        if (current.start !== edited.start) return true;
        if (current.end !== edited.end) return true;
        if (current.hasPause !== edited.hasPause) return true;
        if (current.hasPause && edited.hasPause) {
          if (current.pauseStart !== edited.pauseStart) return true;
          if (current.pauseEnd !== edited.pauseEnd) return true;
        }
      }
    }
    
    return false;
  };
  
  useEffect(() => {
    const hasChanges = checkForChanges();
    onDirtyChange(hasChanges);
  }, [openingHours, currentHours, onDirtyChange]);

  // Mutation zum Aktualisieren der Öffnungszeiten - deutlich vereinfacht
  const updateOpeningHoursMutation = useMutation<OpeningHourApiResponse, Error, any>({
    mutationFn: async (data: any) => {
      console.log("Sende Öffnungszeiten:", data);
      // P0 FIX: Backend erwartet { hours: [...] } oder { openingHours: [...] }
      const payload = { openingHours: data };
      console.log("Payload:", payload);
      
      // Verwende zentrale API-Request-Funktion, damit Auth-Cookies/Fallback korrekt gesetzt sind
      const res = await apiRequest(
        'PUT',
        `/api/restaurants/${restaurant.id}/opening-hours`,
        payload
      );
      // P0: Sichere Response-Parsing
      const responseData = await safeParseResponse<OpeningHourApiResponse>(res);
      if (!responseData) {
        throw new Error("Leere Antwort vom Server");
      }
      console.log("Antwort vom Server:", responseData);
      return responseData;
    },
    onSuccess: async (result) => {
      const savedHours = result.openingHours || result.data || [];
      console.log("Gespeicherte Öffnungszeiten:", savedHours);
      
      // Direkte Aktualisierung statt komplexe Cache-Operationen
      await refetchHours();
      
      // ✅ KORREKTE INVALIDIERUNG: Detail + Sub-Ressourcen + Listen
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: true });
      invalidateAllRestaurantLists(queryClient);
      
      toast({
        title: "Öffnungszeiten gespeichert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });
      
      // P0 FIX: Setze UI-Status zurück NACH Props-Update
      setTimeout(() => {
        onDirtyChange(false);
      }, 100);
    },
    onError: (error: Error) => {
      console.error("Fehler beim Speichern:", error);
      setError(`Fehler beim Speichern: ${error.message}`);
      toast({
        title: "Fehler beim Speichern",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Handler für Änderungen an einem Tag
  const handleHourChange = (index: number, field: keyof EnhancedOpeningHour, value: any) => {
    const newHours = [...openingHours];
    newHours[index] = {
      ...newHours[index],
      [field]: value
    };
    
    setOpeningHours(newHours);
  };

  // Kopiere Öffnungszeiten vom Vortag
  const copyFromPreviousDay = (index: number) => {
    if (index === 0) return; // Montag hat keinen Vortag
    
    const prevDay = openingHours[index - 1];
    const newHours = [...openingHours];
    newHours[index] = { ...prevDay, day: index + 1 };
    
    setOpeningHours(newHours);
  };

  // Kopiere Öffnungszeiten auf alle Tage
  const applyToAll = (sourceIndex: number) => {
    const sourceDay = openingHours[sourceIndex];
    
    const newHours = openingHours.map((_hour, index) => ({
      ...sourceDay,
      day: index + 1
    }));
    
    setOpeningHours(newHours);
  };

  // Standard-Öffnungszeiten anwenden
  const applyDefault = () => {
    const defaultOpenHours = {
      isOpen: true,
      start: "10:00",
      end: "22:00",
      hasPause: false,
      pauseStart: "15:00",
      pauseEnd: "17:00"
    };
    
    const newHours = openingHours.map((hour, index) => ({
      ...hour,
      ...defaultOpenHours,
      day: index + 1
    }));
    
    setOpeningHours(newHours);
  };

  // Werktagsöffnungszeiten (Mo-Fr) und Wochenendöffnungszeiten (Sa-So) anwenden
  const applyWeekdayWeekend = () => {
    const weekdaySettings = {
      isOpen: true,
      start: "09:00",
      end: "20:00",
      hasPause: true,
      pauseStart: "15:00",
      pauseEnd: "17:00"
    };
    
    const weekendSettings = {
      isOpen: true,
      start: "10:00",
      end: "22:00",
      hasPause: false,
      pauseStart: "15:00",
      pauseEnd: "17:00"
    };
    
    const newHours = openingHours.map((hour, index) => ({
      ...hour,
      ...(index < 5 ? weekdaySettings : weekendSettings),
      day: index + 1
    }));
    
    setOpeningHours(newHours);
  };

  // Alle Tage auf "Geschlossen" setzen
  const closeAllDays = () => {
    const newHours = openingHours.map(hour => ({
      ...hour,
      isOpen: false
    }));
    
    setOpeningHours(newHours);
  };

  // Speichere die Öffnungszeiten
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSavingChange(true);
    setError(null);

    try {
      // Konvertiere die erweiterten Öffnungszeiten zurück in das API-Format
      const apiOpeningHours = openingHours
        .filter(hour => hour.isOpen)
        .map(hour => ({
          weekday: hour.day,
          openStart: hour.start,
          openEnd: hour.end,
          pauseStart: hour.hasPause ? hour.pauseStart : null,
          pauseEnd: hour.hasPause ? hour.pauseEnd : null
        }));

      console.log("Speichere Öffnungszeiten für Restaurant-ID:", restaurant.id);
      console.log("Öffnungszeiten-Daten:", JSON.stringify(apiOpeningHours, null, 2));
      
      await updateOpeningHoursMutation.mutateAsync(apiOpeningHours);
    } catch (err) {
      console.error("Fehler beim Aktualisieren der Öffnungszeiten:", err);
      // Fehlerbehandlung wird bereits in onError des Mutations durchgeführt
    } finally {
      onSavingChange(false);
    }
  };

  // Anzahl der geöffneten Tage
  const openDaysCount = openingHours.filter(h => h.isOpen).length;

  return (
    <form id="opening-hours-form" onSubmit={handleSubmit}>
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Öffnungszeiten</CardTitle>
              <CardDescription>
                Verwalten Sie die Öffnungszeiten des Restaurants
              </CardDescription>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge variant={openDaysCount > 0 ? "default" : "destructive"} className="flex gap-1">
                <span>{openDaysCount}</span> von 7 Tagen geöffnet
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            {/* Schnellaktionen */}
            <div className="flex flex-wrap gap-2 mb-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={applyDefault}
                    >
                      <Check className="h-4 w-4 mr-2 text-green-600" />
                      Standard-Öffnungszeiten
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alle Tage auf 10:00 - 22:00 Uhr setzen</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={applyWeekdayWeekend}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Werktag/Wochenende
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Mo-Fr: 9:00-20:00 (mit Pause), Sa-So: 10:00-22:00</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={closeAllDays}
                      className="bg-red-50 hover:bg-red-100 border-red-200"
                    >
                      <Trash className="h-4 w-4 mr-2 text-red-600" />
                      Alle schließen
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alle Tage als geschlossen markieren</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Tabs für Tagesauswahl */}
            <Tabs value={activeDay} onValueChange={setActiveDay}>
              {/* Mobile Fix: kein Wrap (Sa/So wären sonst beide "S") → horizontal scroll */}
              <TabsList
                className="mb-4 w-full flex flex-nowrap overflow-x-auto gap-1 p-1 bg-gray-50 rounded-lg scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <TabsTrigger 
                  value="uebersicht" 
                  className="flex-none min-w-[84px] px-3"
                >
                  <span className="hidden sm:inline">Übersicht</span>
                  <span className="sm:hidden">Alle</span>
                </TabsTrigger>
                {WEEKDAYS.map((day, index) => (
                  <TabsTrigger 
                    key={index} 
                    value={`day-${index}`}
                    className={`flex-none min-w-[56px] text-xs md:text-sm px-3 py-2 ${openingHours[index].isOpen 
                      ? 'data-[state=active]:bg-primary data-[state=active]:text-white' 
                      : 'data-[state=active]:bg-gray-200'}`}
                  >
                    {day.substring(0, 2)}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Übersicht aller Tage */}
              <TabsContent value="uebersicht" className="border rounded-lg p-4">
                {/* Mobile: Day-Cards statt Tabelle */}
                <div className="md:hidden space-y-3">
                  {openingHours.map((hour, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 ${!hour.isOpen ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">{WEEKDAYS[index]}</h4>
                        <Switch
                          checked={hour.isOpen}
                          onCheckedChange={(checked) => handleHourChange(index, 'isOpen', checked)}
                        />
                      </div>
                      {hour.isOpen && (
                        <div className="space-y-3">
                          <div className="flex gap-2 items-center">
                            <label className="text-sm text-gray-600 w-12">Von:</label>
                            <input
                              type="time"
                              value={hour.start}
                              onChange={(e) => handleHourChange(index, 'start', e.target.value)}
                              className="flex-1 p-2 border border-gray-300 rounded"
                            />
                          </div>
                          <div className="flex gap-2 items-center">
                            <label className="text-sm text-gray-600 w-12">Bis:</label>
                            <input
                              type="time"
                              value={hour.end}
                              onChange={(e) => handleHourChange(index, 'end', e.target.value)}
                              className="flex-1 p-2 border border-gray-300 rounded"
                            />
                          </div>
                          <div className="flex gap-2 items-center">
                            <label className="text-sm text-gray-600 w-12">Pause:</label>
                            <Switch
                              checked={hour.hasPause}
                              onCheckedChange={(checked) => handleHourChange(index, 'hasPause', checked)}
                              disabled={!hour.isOpen}
                            />
                            {hour.hasPause && (
                              <div className="flex gap-2 flex-1">
                                <input
                                  type="time"
                                  value={hour.pauseStart}
                                  onChange={(e) => handleHourChange(index, 'pauseStart', e.target.value)}
                                  className="flex-1 p-2 border border-gray-300 rounded"
                                />
                                <span className="self-center text-gray-400">-</span>
                                <input
                                  type="time"
                                  value={hour.pauseEnd}
                                  onChange={(e) => handleHourChange(index, 'pauseEnd', e.target.value)}
                                  className="flex-1 p-2 border border-gray-300 rounded"
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2">
                            {index > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => copyFromPreviousDay(index)}
                                disabled={!hour.isOpen}
                                className="text-xs"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Vom Vortag
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => applyToAll(index)}
                              disabled={!hour.isOpen}
                              className="text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Auf alle
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop: Tabelle */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Tag</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Geöffnet</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Von</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Bis</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Mittagspause</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-500">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {openingHours.map((hour, index) => (
                        <tr key={index} className={!hour.isOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                          <td className="px-4 py-3 font-medium">{WEEKDAYS[index]}</td>
                          <td className="px-4 py-3">
                            <Switch
                              checked={hour.isOpen}
                              onCheckedChange={(checked) => handleHourChange(index, 'isOpen', checked)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="time"
                              value={hour.start}
                              onChange={(e) => handleHourChange(index, 'start', e.target.value)}
                              disabled={!hour.isOpen}
                              className={`p-1.5 border rounded ${!hour.isOpen ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="time"
                              value={hour.end}
                              onChange={(e) => handleHourChange(index, 'end', e.target.value)}
                              disabled={!hour.isOpen}
                              className={`p-1.5 border rounded ${!hour.isOpen ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {hour.isOpen && (
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Switch
                                  checked={hour.hasPause}
                                  onCheckedChange={(checked) => handleHourChange(index, 'hasPause', checked)}
                                  disabled={!hour.isOpen}
                                />
                                {hour.hasPause && (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="time"
                                      value={hour.pauseStart}
                                      onChange={(e) => handleHourChange(index, 'pauseStart', e.target.value)}
                                      className="p-1.5 border rounded border-gray-300 w-24"
                                    />
                                    <span>-</span>
                                    <input
                                      type="time"
                                      value={hour.pauseEnd}
                                      onChange={(e) => handleHourChange(index, 'pauseEnd', e.target.value)}
                                      className="p-1.5 border rounded border-gray-300 w-24"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {index > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyFromPreviousDay(index)}
                                  disabled={!hour.isOpen}
                                >
                                  <Copy className="h-4 w-4" />
                                  <span className="sr-only">Vom Vortag kopieren</span>
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => applyToAll(index)}
                                disabled={!hour.isOpen}
                              >
                                <Plus className="h-4 w-4" />
                                <span className="sr-only">Auf alle anwenden</span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Einzelne Tage */}
              {WEEKDAYS.map((day, index) => (
                <TabsContent key={index} value={`day-${index}`} className="border rounded-lg p-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">{day}</h3>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`switch-${index}`}>Geöffnet:</Label>
                        <Switch
                          id={`switch-${index}`}
                          checked={openingHours[index].isOpen}
                          onCheckedChange={(checked) => handleHourChange(index, 'isOpen', checked)}
                        />
                      </div>
                    </div>

                    {openingHours[index].isOpen && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                          <div>
                            <Label htmlFor={`start-${index}`}>Öffnet um:</Label>
                            <input
                              id={`start-${index}`}
                              type="time"
                              value={openingHours[index].start}
                              onChange={(e) => handleHourChange(index, 'start', e.target.value)}
                              className="w-full mt-1 p-2 border rounded"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`end-${index}`}>Schließt um:</Label>
                            <input
                              id={`end-${index}`}
                              type="time"
                              value={openingHours[index].end}
                              onChange={(e) => handleHourChange(index, 'end', e.target.value)}
                              className="w-full mt-1 p-2 border rounded"
                            />
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Switch
                              id={`pause-switch-${index}`}
                              checked={openingHours[index].hasPause}
                              onCheckedChange={(checked) => handleHourChange(index, 'hasPause', checked)}
                            />
                            <Label htmlFor={`pause-switch-${index}`}>Mittagspause</Label>
                          </div>

                          {openingHours[index].hasPause && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3 pl-6 border-l-2 border-gray-200">
                              <div>
                                <Label htmlFor={`pause-start-${index}`}>Pause beginnt:</Label>
                                <input
                                  id={`pause-start-${index}`}
                                  type="time"
                                  value={openingHours[index].pauseStart}
                                  onChange={(e) => handleHourChange(index, 'pauseStart', e.target.value)}
                                  className="w-full mt-1 p-2 border rounded"
                                />
                              </div>
                              <div>
                                <Label htmlFor={`pause-end-${index}`}>Pause endet:</Label>
                                <input
                                  id={`pause-end-${index}`}
                                  type="time"
                                  value={openingHours[index].pauseEnd}
                                  onChange={(e) => handleHourChange(index, 'pauseEnd', e.target.value)}
                                  className="w-full mt-1 p-2 border rounded"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 mt-6">
                          {index > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => copyFromPreviousDay(index)}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Vom Vortag kopieren
                            </Button>
                          )}
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => applyToAll(index)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Auf alle Tage anwenden
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Desktop save bar — mobile save is in RestaurantEditPage bottom bar */}
      <div className="hidden md:flex justify-end gap-2 mt-6">
        <Button 
          type="submit" 
          disabled={updateOpeningHoursMutation.isPending}
        >
          {updateOpeningHoursMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Wird gespeichert...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Öffnungszeiten speichern
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default RestaurantFormOpeningHours; 